/**
 * Storage Cache Utility
 * 
 * Caches localStorage/sessionStorage reads in memory to avoid
 * expensive synchronous I/O operations on every access.
 * 
 * Best Practice: js-cache-storage
 * Reference: Vercel React Best Practices
 */

const localStorageCache = new Map<string, string | null>();
const sessionStorageCache = new Map<string, string | null>();

/**
 * Get item from localStorage with caching
 */
export function getLocalStorageCached(key: string): string | null {
    if (!localStorageCache.has(key)) {
        localStorageCache.set(key, localStorage.getItem(key));
    }
    return localStorageCache.get(key) ?? null;
}

/**
 * Set item in localStorage and update cache
 */
export function setLocalStorageCached(key: string, value: string): void {
    localStorage.setItem(key, value);
    localStorageCache.set(key, value);
}

/**
 * Remove item from localStorage and cache
 */
export function removeLocalStorageCached(key: string): void {
    localStorage.removeItem(key);
    localStorageCache.delete(key);
}

/**
 * Get item from sessionStorage with caching
 */
export function getSessionStorageCached(key: string): string | null {
    if (!sessionStorageCache.has(key)) {
        sessionStorageCache.set(key, sessionStorage.getItem(key));
    }
    return sessionStorageCache.get(key) ?? null;
}

/**
 * Set item in sessionStorage and update cache
 */
export function setSessionStorageCached(key: string, value: string): void {
    sessionStorage.setItem(key, value);
    sessionStorageCache.set(key, value);
}

/**
 * Remove item from sessionStorage and cache
 */
export function removeSessionStorageCached(key: string): void {
    sessionStorage.removeItem(key);
    sessionStorageCache.delete(key);
}

/**
 * Invalidate cache entry (useful when external changes may occur)
 */
export function invalidateLocalStorageCache(key: string): void {
    localStorageCache.delete(key);
}

/**
 * Clear all caches (useful on logout or tab visibility change)
 */
export function clearStorageCaches(): void {
    localStorageCache.clear();
    sessionStorageCache.clear();
}

// Invalidate cache when storage changes in another tab
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
        if (e.key) {
            localStorageCache.delete(e.key);
            sessionStorageCache.delete(e.key);
        }
    });

    // Clear caches when tab becomes visible (external changes may have occurred)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            clearStorageCaches();
        }
    });
}
